import React, { useContext } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Typography,
  useTheme,
} from "@material-ui/core";
import { assetIds, ThemedAsset, TripleStateToggler } from "nemuru-components";
import MainButton from "../../../../shared/components/MainButton";
import Text from "../../../../shared/components/Text";
import IconTitle from "../../../../shared/components/IconTitle";
import { Controller, useForm } from "react-hook-form";
import { ICON_TITLE_HEIGHT } from "../../../../../config/config";
import { useAccordionStyles } from "../../../../shared/components/InfoPanel";
import { ErrorMessage } from "../../../../shared/components/ErrorMessage";
import { useEffect, useState } from "react";
import ResponsiveDialog from "../../../../shared/components/ResponsiveDialog";
import ResponsiveLayout from "../../../../shared/components/ResponsiveLayout";
import {
  formatEuro,
  getInsuranceRate,
  INSURANCE_PACK,
} from "../config";
import { ProcessContext } from "../../../../../contexts/ProcessContext";

const EXAMPLE_DEBTS = [300, 200, 100];
const EXAMPLE_INSTALMENT = 100;

export const DIALOG_CONTENT = {
  legalTerms: (palette) => (
    <>
      <Box>
        <Box pt={10} style={{ height: "100%" }} />
        <IconTitle
          className="appear-anim"
          icon={
            <ThemedAsset
              id={assetIds.ICON_LOAN_ADJUST}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId={"LEGAL_TERMS"} />}
          subtitle={<Text tId="LEGAL_TERMS_SUBTITLE" />}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="textSecondary">
          <Text tId={"LEGAL_TERMS_DESCRIPTION"} />
        </Typography>
      </Box>
      <Box />
    </>
  ),
  example: (palette, rate, rateLabel) => {
    const headerCellStyle = {
      backgroundColor: palette.primary.main,
      color: palette.primary.contrastText,
      padding: "10px 8px",
      fontWeight: 500,
      fontSize: "0.75rem",
      textAlign: "center",
    };
    const bodyCellStyle = {
      color: palette.text.primary,
      padding: "10px 8px",
      fontSize: "0.8rem",
      textAlign: "center",
    };

    const exampleRows = EXAMPLE_DEBTS.map((debt, index) => {
      const insurance = (debt * rate) / 100;
      return {
        monthTId: `CARD_INSURANCE_EXAMPLE_MONTH_${index + 1}`,
        debt: formatEuro(debt),
        insurance: formatEuro(insurance),
        total: formatEuro(EXAMPLE_INSTALMENT + insurance),
      };
    });

    return (
      <>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            paddingTop: "48px",
          }}
        >
          <Typography
            variant="subtitle1"
            style={{
              color: palette.text.title,
              textAlign: "center",
            }}
          >
            <Text strong tId="WHAT_IS_THE_INSURANCE_COST" />
          </Typography>
          <Typography
            variant="caption"
            style={{
              color: palette.text.primary,
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            <Text
              tId="CARD_INSURANCE_EXAMPLE_CAPTION_TOP"
              dynamicDataTexts={[rateLabel]}
            />
          </Typography>
          <Box
            style={{
              width: "100%",
              maxWidth: "420px",
              border: "1px solid #e0e0e0",
              overflow: "hidden",
              backgroundColor: "#ffffff",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      ...headerCellStyle,
                      textAlign: "left",
                      minWidth: "60px",
                    }}
                  >
                    <Text tId="CARD_INSURANCE_EXAMPLE_COL_MONTH" />
                  </th>
                  <th style={headerCellStyle}>
                    <Text tId="CARD_INSURANCE_EXAMPLE_COL_DEBT" />
                  </th>
                  <th style={headerCellStyle}>
                    <Text tId="CARD_INSURANCE_EXAMPLE_COL_INSURANCE" />
                  </th>
                  <th style={headerCellStyle}>
                    <Text tId="CARD_INSURANCE_EXAMPLE_COL_TOTAL" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {exampleRows.map((row) => (
                  <tr key={row.monthTId}>
                    <td style={{ ...bodyCellStyle, textAlign: "left" }}>
                      <Text tId={row.monthTId} />
                    </td>
                    <td style={bodyCellStyle}>{row.debt}</td>
                    <td style={bodyCellStyle}>{row.insurance}</td>
                    <td style={bodyCellStyle}>{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <Typography
            variant="caption"
            style={{
              color: palette.text.primary,
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            <Text tId="CARD_INSURANCE_EXAMPLE_CAPTION_BOTTOM" />
          </Typography>
        </Box>
      </>
    );
  },
};

export const CardInsurance = ({ onSubmitForm, insurancePack }) => {
  const { palette, spacing } = useTheme();
  const {
    state: { flowLoading },
  } = useContext(ProcessContext);

  const {
    getValues,
    handleSubmit,
    setValue,
    watch,
    control,
    errors,
    clearErrors,
  } = useForm();

  const classes = useAccordionStyles();

  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);

  const cardInsuranceWatcher = watch("cardInsurance");
  const insuranceRate = getInsuranceRate(insurancePack);
  const rateLabel = formatEuro(insuranceRate);

  useEffect(() => {
    if (cardInsuranceWatcher !== null || cardInsuranceWatcher !== undefined) {
      clearErrors("cardInsurance");
    }
  }, [cardInsuranceWatcher]);

  const customAccordionStyle = {
    minHeight: "46px",
    maxWidth: "calc(100% - 6px)",
  };
  const accordionDetailsStyle = {
    paddingLeft: "40px",
  };

  return (
    <Box
      style={{
        display: flowLoading.show ? "none" : "block",
      }}
    >
      <Box>
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_MONEY}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId={`CARD_INSURANCE`} />}
          subtitle={<Text tId={`CARD_INSURANCE_SUBTITLE`} />}
        />
        <Typography variant="subtitle2" color="textSecondary">
          <Text tId={`CARD_INSURANCE_DESCRIPTION_START`} />
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          <Text
            tId={"CARD_INSURANCE_CONDITIONS"}
            dynamicDataTexts={[rateLabel]}
          />
        </Typography>{" "}
        <Box
          my={1}
          onClick={() => {
            setOpen(true);
            setDialogContent("example");
          }}
          tabIndex={0}
          role="button"
          style={{ cursor: "pointer" }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setOpen(true);
              setDialogContent("example");
            }
          }}
        >
          <Typography variant="subtitle2" color="primary">
            <Text tId={"SEE_EXAMPLE"} />
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary">
          <Text tId={`CARD_INSURANCE_DESCRIPTION_END`} />
        </Typography>
        <Box mt={3}>
          {insurancePack === INSURANCE_PACK.PROTECTION_PLUS && (
            <Accordion
              elevation={0}
              classes={{
                root: classes.MuiAccordionroot,
              }}
              style={customAccordionStyle}
            >
              <AccordionSummary
                expandIcon={<i className="material-icons">expand_more</i>}
                classes={{
                  root: classes.MuiAccordionSummaryroot,
                  expandIcon: classes.MuiAccordionSummaryexpandIcon,
                  content: classes.MuiAccordionSummarycontent,
                }}
                style={customAccordionStyle}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  gridGap={8}
                >
                  <i
                    className="material-icons"
                    style={{
                      fontSize: "32px",
                      color: palette.primary.main,
                    }}
                  >
                    assured_workload
                  </i>
                  <Typography variant="subtitle1" color="textPrimary">
                    <Text strong tId={"UNEMPLOYMENT_PROTECTION_INSURANCE"} />
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails style={accordionDetailsStyle}>
                <Typography variant="subtitle2" color="textSecondary">
                  <Text tId={"UNEMPLOYMENT_PROTECTION_INSURANCE_DESCRIPTION"} />
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
          <Accordion
            elevation={0}
            classes={{
              root: classes.MuiAccordionroot,
            }}
            style={customAccordionStyle}
          >
            <AccordionSummary
              expandIcon={<i className="material-icons">expand_more</i>}
              classes={{
                root: classes.MuiAccordionSummaryroot,
                expandIcon: classes.MuiAccordionSummaryexpandIcon,
                content: classes.MuiAccordionSummarycontent,
              }}
              style={customAccordionStyle}
            >
              <Box
                display="flex"
                alignItems="center"
                flexDirection="row"
                gridGap={8}
              >
                <i
                  className="material-icons"
                  style={{
                    fontSize: "32px",
                    color: palette.primary.main,
                  }}
                >
                  personal_injury
                </i>
                <Typography variant="subtitle1" color="textPrimary">
                  <Text strong tId={"PAYMENT_PROTECTION_INSURANCE"} />
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails style={accordionDetailsStyle}>
              <Typography variant="subtitle2" color="textSecondary">
                <Text
                  tId={`PAYMENT_PROTECTION_INSURANCE_DESCRIPTION_${insurancePack}`}
                />
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          mt={2}
          mb={1}
          onClick={() => {
            setOpen(true);
            setDialogContent("legalTerms");
          }}
          tabIndex={0}
          role="button"
          style={{ cursor: "pointer" }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setOpen(true);
              setDialogContent("legalTerms");
            }
          }}
        >
          <Typography variant="subtitle2" color="primary">
            <Text tId={"SEE_LEGAL_TERMS"} />
          </Typography>
        </Box>
      </Box>
      <Box>
        <Controller
          defaultValue={cardInsuranceWatcher || null}
          name={"cardInsurance"}
          control={control}
          rules={{
            validate: {
              validate: (value) => {
                if (value !== null) return;
                return (
                  <Text tId={"PLEASE_SELECT_IF_YOU_WANT_TO_HAVE_INSURANCE"} />
                );
              },
            },
          }}
          render={() => (
            <Box
              minHeight={"42px"}
              container={"true"}
              className={"mb-1"}
              style={{
                display: "inline-flex",
                width: "100%",
                marginTop: spacing(1),
                alignItems: "flex-start",
              }}
            >
              <TripleStateToggler
                value={cardInsuranceWatcher || null}
                name={"cardInsurance"}
                key={"cardInsurance"}
                handleChange={(_, value) => {
                  setValue("cardInsurance", value);
                }}
              />
              <Typography
                variant={"body2"}
                color="textSecondary"
                style={{
                  wordWrap: "anywhere",
                  lineHeight: 1.5,
                }}
              >
                <Text strong tId={"DO_YOU_WANT_TO_HAVE_INSURANCE"} />
                {errors.cardInsurance && (
                  <ErrorMessage errorMsg={errors.cardInsurance.message} />
                )}
              </Typography>
            </Box>
          )}
        />
        <MainButton
          color={cardInsuranceWatcher ? "primary" : "secondary"}
          variant={cardInsuranceWatcher === null ? "outlined" : "contained"}
          onClick={handleSubmit(() => onSubmitForm(getValues()))}
        >
          <Text
            tId={
              cardInsuranceWatcher === null
                ? "APPLY_FOR_LOAN"
                : cardInsuranceWatcher === true
                ? "APPLY_WITH_INSURANCE"
                : "APPLY_WITHOUT_INSURANCE"
            }
          />
        </MainButton>
      </Box>
      <ResponsiveDialog
        displayCondition={true}
        fullScreenCondition={true}
        onClose={() => {
          setOpen(false);
          setDialogContent(null);
        }}
        open={open}
      >
        <Container
          style={{ padding: "1rem 1rem", height: "100%", left: "-15px" }}
        >
          <ResponsiveLayout>
            {dialogContent &&
              DIALOG_CONTENT[dialogContent](
                palette,
                insuranceRate,
                rateLabel
              )}
          </ResponsiveLayout>
        </Container>
      </ResponsiveDialog>
    </Box>
  );
};
